import React from "react";
import {
  Button,
  Iconography,
  IconographySizes,
  IconographyThemes,
} from "@hid-galaxy-ui/galaxy-react";
import { Chip, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import "./ValidationStatusTable.scss";

export type ValidationStatusRow = {
  ID: number;
  partner_name: string;
  technology: string;
  integration: string;
  validation_status: 0 | 1;
};

type Props = {
  rows: ValidationStatusRow[];
  searchTerm: string;
  statusFilter: "all" | "true" | "false";
  onFilteredRowsChange?: (rows: ValidationStatusRow[]) => void;
  currentPageNumber: number;
  rowsPerPage: number;
  onEdit: (row: ValidationStatusRow) => void;
  onDelete: (row: ValidationStatusRow) => void;
};

const matchesSearch = (row: ValidationStatusRow, raw: string) => {
  const q = raw.trim().toLowerCase();
  if (!q) return true;
  const statusText = row.validation_status === 1 ? "true" : "false";
  return (
    row.partner_name.toLowerCase().includes(q) ||
    row.technology.toLowerCase().includes(q) ||
    row.integration.toLowerCase().includes(q) ||
    statusText.includes(q)
  );
};

const ValidationStatusTable: React.FC<Props> = ({
  rows,
  searchTerm,
  statusFilter,
  onFilteredRowsChange,
  currentPageNumber,
  rowsPerPage,
  onEdit,
  onDelete,
}) => {
  const filteredRows = React.useMemo(() => {
    return rows.filter((row) => {
      const statusOk =
        statusFilter === "all"
          ? true
          : statusFilter === "true"
          ? row.validation_status === 1
          : row.validation_status === 0;
      return statusOk && matchesSearch(row, searchTerm);
    });
  }, [rows, searchTerm, statusFilter]);

  React.useEffect(() => {
    onFilteredRowsChange?.(filteredRows);
  }, [filteredRows, onFilteredRowsChange]);

  const paginatedRows = React.useMemo(() => {
    const start = (currentPageNumber - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, currentPageNumber, rowsPerPage]);

  return (
    <div className="validation-status-table-container">
      <Table className="validation-status-table">
        <TableHead>
          <TableRow className="validation-status-table__head-row">
            <TableCell className="validation-status-table__head-cell">
              Partner Name
            </TableCell>
            <TableCell className="validation-status-table__head-cell">
              Technology
            </TableCell>
            <TableCell className="validation-status-table__head-cell">
              Integration
            </TableCell>
            <TableCell className="validation-status-table__head-cell">
              Validation Status
            </TableCell>
            <TableCell className="validation-status-table__head-cell">
              Action
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedRows.length > 0 ? (
            paginatedRows.map((row) => (
              <TableRow
                key={row.ID}
                className="validation-status-table__body-row"
              >
                <TableCell className="validation-status-table__body-cell">
                  {row.partner_name}
                </TableCell>
                <TableCell className="validation-status-table__body-cell">
                  {row.technology}
                </TableCell>
                <TableCell className="validation-status-table__body-cell">
                  {row.integration}
                </TableCell>
                <TableCell className="validation-status-table__body-cell">
                  <Chip
                    size="small"
                    label={row.validation_status === 1 ? "TRUE" : "FALSE"}
                    className={
                      row.validation_status === 1
                        ? "status-chip status-chip--true"
                        : "status-chip status-chip--false"
                    }
                  />
                </TableCell>
                <TableCell className="validation-status-table__body-cell">
                  <div className="action-buttons">
                    <Button
                      variant="secondary"
                      icon={
                        <Iconography
                          icon="userPen"
                          size={IconographySizes.Medium}
                          theme={IconographyThemes.Info}
                        />
                      }
                      className="icon-only-action user-edit-button"
                      onClick={() => onEdit(row)}
                    />
                    <Button
                      variant="secondary"
                      icon={
                        <Iconography
                          icon="trashCan"
                          size={IconographySizes.Medium}
                          theme={IconographyThemes.Error}
                        />
                      }
                      className="icon-only-action delete-button"
                      onClick={() => onDelete(row)}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow className="validation-status-table__body-row">
              <TableCell
                className="validation-status-table__body-cell validation-status-table__no-data"
                colSpan={5}
              >
                No data available for display.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ValidationStatusTable;

